const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/utils/data.ts';
import Imm from 'immutable';
import { OptionEntry, OptionObject, RadioButtonOptionEntry } from '../types';
import _ from 'lodash';
import moment from 'moment/moment';
import { KeyValuePair, KISIM_TIME_LOWER_LIMIT, KISIM_TIME_OPEN } from '../../../defs/generalDefs';
import { EventData, MetaKatItem } from '../../../defs/kisim';
import { isEmpty } from '../../../utils/generalUtils';

const emptyMap = Imm.Map<string, any>();

type FormFieldValue = string | null | undefined | KeyValuePair;

export const isValue = (value: FormFieldValue) => !isEmpty(value);

export const atLeastOneHasValue = (formData: KeyValuePair, paths: string[]) => {
    return paths.some((path: string) => isValue(_.get(formData, path)));
};

export const booleanToString = function (value: boolean | undefined, alternativeTrueValue?: string): string {
    if (typeof value === 'boolean' && value) {
        return alternativeTrueValue ?? '1';
    }
    return '';
};

export const stringToBoolean = function (value: string | undefined): boolean {
    return !!value;
};

// This is supposed to be a nice generic function meant for CIS4m data that should be strings,
// HOWEVER, old PatApp ECheckin stores false as boolean in the data structure....
export const valueToBoolean = function (value: undefined | null | boolean | string): boolean {
    const falseStringValues = ['', '0', 'false'];

    return typeof value === 'string'
        ? !falseStringValues.includes(value.toLowerCase())
        : ![undefined, null, false].includes(value);
};

export const mapArrayToObject = function (array: string[] | null): OptionObject {
    if (!array || array?.length < 1) {
        return {};
    }
    return array.reduce((obj, cur) => ({ ...obj, [cur]: '1' }), {});
};

export const mapObjectToArray = function (obj: Imm.Map<string, any> | undefined): string[] | null {
    if (!obj) {
        return [];
    }
    return obj.keySeq().toArray();
};

export const mapOptionToObject = function (value: string | null): OptionObject | RadioButtonOptionEntry {
    if (!value) {
        return {};
    }
    return {
        [value]: '1',
    };
};

export const mapObjectToOption = function (
    obj: Imm.Map<string, any>,
    options: (OptionEntry | RadioButtonOptionEntry)[]
): string {
    if (!obj) {
        return '';
    }
    let result: string = '';
    obj.forEach((_entry: any, name: string) => {
        // Only show the options you are allowed to
        if (!!options.find((option: OptionEntry | RadioButtonOptionEntry) => option.value === name)) {
            result = name;
        }
    });
    return result;
};

export const changePath = (
    readyData: any,
    sourcePath: string,
    targetPath: string,
    mergeIntoTarget: boolean = false
): string => {
    const value = _.get(readyData, sourcePath);
    if (value) {
        if (typeof value === 'object' && mergeIntoTarget) {
            Object.keys(value).forEach(key => {
                _.set(readyData, `${targetPath}.${key}`, value[key]);
            });
        } else {
            _.set(readyData, targetPath, value);
        }
    }
    _.unset(readyData, sourcePath);
    return value;
};

export const changePathFiltered = (
    object: KeyValuePair,
    sourcePath: string,
    filteredProps: string[],
    targetPath: string,
    sourceSuffix?: string,
    targetSuffix?: string
) => {
    const values = _.get(object, sourcePath);
    if (values && typeof values === 'object') {
        Object.keys(values).forEach(key => {
            if (filteredProps.indexOf(key) === -1) return;
            changePath(
                object,
                _.compact([sourcePath, key, sourceSuffix]).join('.'),
                _.compact([targetPath, key, targetSuffix]).join('.')
            );
        });
    }
};

export const duplicateProperty = (data: any, sourcePath: string, targetPath: string) => {
    const value = _.get(data, sourcePath);
    _.set(data, targetPath, value);
    return value;
};

export const removeEmptyPropertiesFromData = (data: KeyValuePair) => {
    const removeEmptyProperties = (object: KeyValuePair, path: string[] = []) => {
        Object.keys(object).forEach((key: string) => {
            path.push(key);
            const propValue = object[key];
            if (propValue === '' || propValue === null || propValue === undefined) {
                _.unset(data, path.join('.'));
            } else if (typeof propValue === 'object') {
                removeEmptyProperties(propValue, path);
                if (Object.keys(propValue).length === 0) {
                    _.unset(data, path.join('.'));
                }
            }
            path.pop();
        });
    };
    removeEmptyProperties(data);
};

// FIXME: MOVE ALL UTILS BELOW TO A BETTER LOCATION... THESE ARE NOT FORM RELATED UTILS
// Also check remaining utils because some of them are also not form related....

export const serializeMetakatValue = function (value: MetaKatItem | null | undefined): string {
    if (!value) {
        return '';
    }
    return `${escape(value.KAT)}#${escape(value.FKEY)}#${escape(value.NAME)}`;
};

export const parseMetakatValueString = function (value: string): MetaKatItem {
    const fields = value.split('#');
    return {
        FKEY: unescape(fields[1] || ''),
        ICODE: '',
        KAT: unescape(fields[0] || ''),
        LEVEL: '',
        NAME: unescape(fields[2] || ''),
        PCODE: '',
        STATUS: '',
    };
};

export const isKidVersion = (
    refDate?: string,
    gebDate?: string,
    customAgeDef?: number,
    orderDemandsKidStuff?: boolean
) => {
    let patientIsBaby = false;
    if (gebDate) {
        patientIsBaby =
            customAgeDef === undefined
                ? moment(refDate).diff(moment(gebDate), 'years') < 1
                : moment(refDate).diff(moment(gebDate), 'months') / 12 < customAgeDef; // in "months" since, customAgeDef can be decimal
    }
    return patientIsBaby || orderDemandsKidStuff;
};

export const isWithinBoundaries = (value: number, minVal: number, maxVal: number) => {
    if (value <= maxVal && value >= minVal) {
        return true;
    }
    return false;
};

export const setLEPContent = (readyData: EventData, config: any): void => {
    const LEPDuration = _.get(readyData, 'CONTENT.IntDauer.Val');
    const numberOfPeople = _.get(readyData, 'CONTENT.AnzPers.Val');
    if (!!config.LEP?.duration?.hidden) {
        _.unset(readyData, 'CONTENT.IntDauer');
    } else if (!!LEPDuration) {
        _.set(readyData, 'CONTENT.IntDauer.BezWert', 'LEP-Dauer');
        _.set(readyData, 'CONTENT.IntDauer.Unit', 'Min');
    }

    if (!!config.LEP?.numberOfPeople?.hidden) {
        _.unset(readyData, 'CONTENT.AnzPers');
    } else if (!!numberOfPeople) {
        _.set(readyData, 'CONTENT.AnzPers.BezWert', 'Personen');
        _.set(readyData, 'CONTENT.AnzPers.Unit', '');
    }
};

// Utils for questionnaire

/**
 * Check if chosen time should be within the case duration.
 * @param date
 * @param eindat
 * @param ausdat
 */
export const checkDateInCase = (date: string, eindat: string, ausdat: string) => {
    let errorText = '';

    if (eindat > date || (ausdat < date && !!ausdat)) {
        errorText = 'Datum ausserhalb Falldauer.';
    }
    return errorText;
};

/**
 * Checks all the events with the caseNr and returns an errorText if at least one match has been found
 * @param caseNr
 * @param events
 */
export const checkCaseUniqueness = (caseNr: string, events: Imm.Map<string, any>) => {
    let errorText = '';
    if (events.size > 0 && events.find((event: Imm.Map<string, any>) => event.get('FALLNR') === caseNr)) {
        errorText = 'Es gibt zu diesem Fall bereits ein Formular.';
    }

    return errorText;
};

export const filterByCase = (caseNr: string, events: Imm.Map<string, any>) => {
    return (events || emptyMap).filter((record: Imm.Map<string, any>) => {
        if (caseNr) {
            const fallnr = record?.get('FALLNR') || record?.getIn(['Source', 'FALLNR']);
            if (!fallnr) {
                return true; // not fallnr defined, therefore no check possible (e.g. diagnoses, imaging)
            }
            return caseNr === fallnr;
        }
        return true;
    });
};

/**
 * Checks the date with the discharge events and if it was conducted after a certain amount of days after entry
 * @param date
 * @param eindat date of entry
 * @param dischargeEvents all discharge events
 * @param curCaseNr
 * @param allowedMaxDaysConductionAfterEntry
 * @param errorTextDateAfterDischarge
 */
export const checkDateForEntry = (
    date: string,
    eindat: string,
    dischargeEvents: Imm.Map<string, any>,
    curCaseNr: string,
    errorTextDateAfterDischarge: string,
    allowedMaxDaysConductionAfterEntry?: number
) => {
    let errorText = '';

    const dischargeEventsByCurrentCase = filterByCase(curCaseNr, dischargeEvents);

    // Start date has to be before the start date of a possible existing discharge event.
    if (dischargeEventsByCurrentCase.size > 0) {
        dischargeEventsByCurrentCase.forEach((record: Imm.Map<string, any>) => {
            const dischargeStartDate = record.get('STARTDAT');
            if (date >= dischargeStartDate) {
                errorText = errorText && errorText.concat(' ');
                errorText = errorText.concat(errorTextDateAfterDischarge);
            }
        });
    }

    if (
        eindat &&
        allowedMaxDaysConductionAfterEntry &&
        eindat > KISIM_TIME_LOWER_LIMIT &&
        _.isNumber(allowedMaxDaysConductionAfterEntry)
    ) {
        const dateDiff = moment(date).diff(moment(eindat), 'minutes'); // eindat + dateDiff = date
        if (dateDiff > allowedMaxDaysConductionAfterEntry * 24 * 60) {
            errorText = errorText && errorText.concat(' ');
            errorText = errorText.concat('Datum mehr als ' + Math.floor(dateDiff / (60 * 24)) + ' Tage nach Eintritt.');
        }
    }

    if (errorText) {
        errorText = errorText && errorText.concat(' ');
        errorText = errorText.concat('Speichern nicht möglich.');
    }
    return errorText;
};

/**
 * Checks the date with the entry events and if it was conducted before a certain amount of days before discharge
 * @param date
 * @param ausdat date of discharge
 * @param entryEvents all entry events
 * @param curCaseNr
 * @param allowedMaxDaysConductionAfterEntry
 * @param errorTextDateAfterDischarge
 */
export const checkDateForDischarge = (
    date: string,
    ausdat: string,
    entryEvents: Imm.Map<string, any>,
    curCaseNr: string,
    errorTextDateBeforeEntry: string,
    allowedMaxDaysConductionBeforeDischarge?: number
) => {
    let errorText = '';

    const entryEventsByCurrentCase = filterByCase(curCaseNr, entryEvents);

    // Start date has to be after the start date of a possible existing entry event.
    if (entryEventsByCurrentCase.size > 0) {
        entryEventsByCurrentCase.forEach((record: Imm.Map<string, any>) => {
            const entryStartDate = record.get('STARTDAT');
            if (date < entryStartDate) {
                errorText = errorText && errorText.concat(' ');
                errorText = errorText.concat(errorTextDateBeforeEntry);
            }
        });
    }

    if (
        ausdat &&
        allowedMaxDaysConductionBeforeDischarge &&
        ausdat < KISIM_TIME_OPEN &&
        _.isNumber(allowedMaxDaysConductionBeforeDischarge)
    ) {
        const dateDiff = moment(ausdat).diff(moment(date), 'minutes'); // date + dateDiff = ausdat
        if (dateDiff > allowedMaxDaysConductionBeforeDischarge * 24 * 60) {
            errorText = errorText && errorText.concat(' ');
            errorText = errorText.concat('Datum mehr als ' + Math.floor(dateDiff / (60 * 24)) + ' Tage vor Austritt.');
        }
    }

    if (errorText) {
        errorText = errorText && errorText.concat(' ');
        errorText = errorText.concat('Speichern nicht möglich.');
    }
    return errorText;
};

export const getQuestionnaireFkeyByFcode = (fcode: string) => {
    if (fcode.includes('BSCEF')) {
        return 'BSCEF';
    } else if (fcode.includes('BSCAF')) {
        return 'BSCAF';
    } else if (fcode.includes('HONES')) {
        return 'HONES';
    } else if (fcode.includes('HONAS')) {
        return 'HONAS';
    }
};

export const round = (value: number, precision: number) => {
    const multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
};

export const calcSurface = (length: number, width: number, precision: number = 2) => {
    return round(((length * width) / 4) * Math.PI, precision);
};
